import { z } from 'zod';

import { Flavor, GenerationId } from 'backend-client-web';

import objectPassthrough from '../objectPassthrough';
import { Base64String } from '../types';
import { newUuid, UUID } from '../uuid';

export type ContentId = Flavor<UUID, 'ContentId'>;

interface ProtectedValue {
  readonly contentId: ContentId;
  readonly encrypted: Base64String;
  readonly isEmpty?: boolean;
}

const ProtectedValue = objectPassthrough({
  // Content identity only changes on user edits. The field should be set most of the time, since
  // it was set for all ProtectedValues during the automerge migration. However contentId-less
  // fields may have been re-introduced after the heymerge migration. In this case we generate an
  // id during parsing.
  contentId: z.uuid().default(newUuid),
  encrypted: z.base64(),
  isEmpty: z.boolean().optional(),
});

interface UnprotectedValue {
  readonly contentId: ContentId;
  readonly unencrypted: string;
  // This field stores the encrypted value that this was unprotected from, if it came from a vault.
  // This allows reusing the encrypted data if the unprotected value is later reencrypted for the
  // same vault.
  readonly reencryptCachedValue?: {
    // This identifies the protected vault secret used to encrypt the value below. It must be
    // consistent, or bad things will happen!
    readonly vaultGenerationId: GenerationId;
    // We duplicate the contentId value here, to avoid scenarios where the reencryptCachedValue is
    // spread into a different UnprotectedValue and then incorrectly used. Note we can't name this
    // field "contentId", otherwise the recursive Unprotect type below will consider this object a
    // ProtectedValue, and we specifically don't want that to happen.
    readonly originalContentId: ContentId;
    readonly encrypted: Base64String;
  };
}

const UnprotectedValue = objectPassthrough({
  // See ProtectedValue.contentId. Note that this field was previously `UUID | null` and therefore
  // uses a different transformation here.
  contentId: z.union([z.uuid(), z.null()]).transform((arg) => (arg === null ? newUuid() : arg)),
  unencrypted: z.string(),
  reencryptCachedValue: z
    .object({
      vaultGenerationId: z.uuid(),
      originalContentId: z.uuid(),
      encrypted: z.base64(),
    })
    .optional(),
});

type Unprotect<T, R = string> =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  T extends Function ?
    T // eslint-disable-next-line @typescript-eslint/no-shadow
  : T extends Array<infer T> ? UnprotectArray<T, R>
  : T extends string & infer O ? string & O
  : T extends ProtectedValue ? R
  : T extends object ? UnprotectObject<T, R>
  : T;

type UnprotectArray<T, R> = Array<Unprotect<T, R>>;
type UnprotectObject<T, R> = { [P in keyof T]: Unprotect<T[P], R> };

export { ProtectedValue, type Unprotect, UnprotectedValue };
